/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import slugify from 'slugify';

import { getImageAtSize } from '../../lib/utils';

import { useThemeClassName } from '../../contexts/ThemeContext';
import HangmanResponse from '../partials/HangmanResponse';

import styles from '../../../styles/questions/hangman.module.scss';

import background from '../../../img/iga/noel/hangman-bg.svg';

interface HangmanQuestionProps {
    game?: HangmanGame;
    question?: Question;
    answer?: Answer;
    className?: string | null;
}

function HangmanQuestion({
    game = null,
    question = null,
    answer = null,
    className = null,
}: HangmanQuestionProps) {
    const themeClassName = useThemeClassName(styles);

    const { errorLimit, hangmanImages: images } = game || {};
    const { body: questionBody = null } = question || {};
    const { value: letters = [] } = answer || {};

    const userLetters = useMemo(
        () =>
            (letters || []).map((letter) => ({
                value: letter,
                valid: slugify(questionBody).toLowerCase().indexOf(slugify(letter)) !== -1,
            })),
        [letters, questionBody],
    );

    const errorCount = useMemo(
        () =>
            (userLetters || []).filter(({ valid: validLetter = null }) => validLetter === false)
                .length,
        [userLetters],
    );

    const { body = null, hint = null } = question || {};

    // console.log('question', question);

    const currentImage =
        images !== null && errorCount < images.length ? images[errorCount] || null : null;
    const mediumImage = getImageAtSize(currentImage, 'medium');
    const { url: mediumImageUrl = null } = mediumImage || {};

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            <div className={styles.line}>
                <div className={styles.lineInner}>
                    {hint !== null ? (
                        <div className={styles.hint}>
                            <span className={styles.hintLabel}>
                                <FormattedMessage
                                    defaultMessage="Hint:"
                                    description="Question hint label"
                                />
                            </span>{' '}
                            {hint}
                        </div>
                    ) : null}
                    <div className={styles.errorCount}>
                        <span className={styles.currentErrors}>{errorCount}</span>/
                        <span className={styles.errorLimit}>{errorLimit}</span>
                    </div>
                </div>
            </div>
            <div className={styles.inner}>
                <HangmanResponse word={body} className={styles.response} />
                <div className={styles.imageContainer}>
                    {background !== null ? (
                        <img
                            src={background}
                            alt=""
                            className={classNames([
                                styles.backgroundImage,
                                'game-background-image',
                            ])}
                        />
                    ) : null}
                    {mediumImageUrl !== null ? (
                        <img
                            src={mediumImageUrl}
                            alt={errorCount}
                            className={classNames([styles.image, 'game-image'])}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default HangmanQuestion;
